import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Heart, Loader2, ExternalLink, Users, Landmark, Globe2, Mountain, ScrollText,
  Building2, TrendingUp, Wheat, UtensilsCrossed, Palette, GraduationCap, HeartPulse,
  TrainFront, Plane, MapPin, Info,
} from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import ListenButton from "./ListenButton";
import ShareBadge from "./ShareBadge";
import Discussions from "./Discussions";
import CountryNews from "./CountryNews";
import PhotoGallery from "./PhotoGallery";
import SuggestCorrection from "./SuggestCorrection";
import Seo from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sp_token")}` });

const SECTION_META = [
  { id: "overview", title: "Overview", icon: Globe2, eco: { label: "Photos on PhotoPulse.pro", url: "https://photopulse.pro" } },
  { id: "geography", title: "Geography & Nature", icon: Mountain, eco: { label: "Wildlife & nature on NaturePulse.net", url: "https://naturepulse.net" } },
  { id: "history", title: "History", icon: ScrollText },
  { id: "government", title: "Government & Civic Structure", icon: Building2 },
  { id: "economy", title: "Economy", icon: TrendingUp },
  { id: "agriculture", title: "Agriculture", icon: Wheat, eco: { label: "Explore farming on FarmersPulse.com", url: "https://farmerspulse.com" } },
  { id: "food", title: "Food & Cuisine", icon: UtensilsCrossed, eco: { label: "Discover dishes on FoodPulse.net", url: "https://foodpulse.net" } },
  { id: "culture", title: "Culture & Society", icon: Palette, eco: { label: "Stories & voices on SocialPulse.blog", url: "https://socialpulse.blog" } },
  { id: "education", title: "Education", icon: GraduationCap },
  { id: "health", title: "Health", icon: HeartPulse },
  { id: "transportation", title: "Transportation", icon: TrainFront },
  { id: "tourism", title: "Travel & Tourism", icon: Plane, eco: { label: "Plan your trip on TravelerPulse.pro", url: "https://travelerpulse.pro" } },
];

function QuickFacts({ c, tr }) {
  const facts = [
    ["Capital", c.capital],
    ["Region", `${c.region}${c.subregion ? " · " + c.subregion : ""}`],
    ["Population", c.population ? `${c.population.toLocaleString()} (${c.population_year}, World Bank)` : null],
    ["Area", c.area ? `${c.area.toLocaleString()} km²` : null],
    ["Languages", c.languages?.slice(0, 5).join(", ")],
    ["Currency", c.currencies?.map((x) => `${x.name}${x.symbol ? " (" + x.symbol + ")" : ""}`).slice(0, 2).join(", ")],
    ["Calling code", c.calling_codes?.slice(0, 2).join(", ")],
    ["Internet TLD", c.tld?.join(", ")],
    ["Demonym", c.demonym],
    ["ISO codes", `${c.cca2} · ${c.cca3}${c.ccn3 ? " · " + c.ccn3 : ""}`],
    ["UN member", c.un_member ? "Yes" : "No"],
    ["Landlocked", c.landlocked ? "Yes" : "No"],
  ].filter(([, v]) => v);
  return (
    <div data-testid="country-quick-facts" className="border border-[#BFD4F2] rounded-3xl p-5 bg-white">
      <span className="flex items-center gap-2 font-bold text-sm text-[#1D4ED8]"><Info size={15} /> {tr("Quick Facts")}</span>
      <div className="mt-3 space-y-2.5">
        {facts.map(([label, value]) => (
          <div key={label} className="text-sm leading-relaxed">
            <span className="font-extrabold text-[#0F2557]">{tr(label)}: </span>
            <span className="text-[#334155]">{value}</span>
          </div>
        ))}
      </div>
      <p className="text-[0.68rem] text-[#64748B] mt-3 leading-relaxed">
        {tr("Sources")}: ISO 3166 · World Bank · {tr("retrieved")} {c.retrieved_at?.slice(0, 10)}
      </p>
    </div>
  );
}

export default function CountryPage() {
  const { slug } = useParams();
  const { tr } = useI18n();
  const { user } = useAuth();
  const [country, setCountry] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sections, setSections] = useState(null);
  const [cities, setCities] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [following, setFollowing] = useState(false);
  const [statesInfo, setStatesInfo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCountry(null);
    setSections(null);
    setNotFound(false);
    axios.get(`${API}/country/${encodeURIComponent(slug)}`)
      .then((r) => {
        setCountry(r.data);
        if (r.data.sections) {
          setSections(r.data.sections);
          setCities(r.data.cities || []);
        } else {
          setGenerating(true);
          axios.post(`${API}/country/${encodeURIComponent(slug)}/enrich`, {}, { timeout: 180000 })
            .then((e) => { setSections(e.data.sections); setCities(e.data.cities || []); })
            .catch(() => toast.error("Content generation is busy — please refresh in a moment."))
            .finally(() => setGenerating(false));
        }
      })
      .catch(() => setNotFound(true));
    axios.get(`${API}/country/${encodeURIComponent(slug)}/states`)
      .then((r) => setStatesInfo(r.data))
      .catch(() => setStatesInfo(null));
  }, [slug]);

  useEffect(() => {
    if (!user || !country) return;
    axios.get(`${API}/follows`, { headers: authHeaders() })
      .then((r) => setFollowing(r.data.some((f) => f.place_name === country.name)))
      .catch(() => {});
  }, [user, country]);

  const jsonLd = useMemo(() => country && {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Country", name: country.name, alternateName: country.official,
        identifier: country.cca2, url: `https://worldpulse.pro/country/${country.slug}`,
        ...(country.photo ? { image: country.photo.url } : {}) },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "World", item: "https://worldpulse.pro/" },
        { "@type": "ListItem", position: 2, name: country.region, item: "https://worldpulse.pro/countries" },
        { "@type": "ListItem", position: 3, name: country.name, item: `https://worldpulse.pro/country/${country.slug}` },
      ] },
    ],
  }, [country]);

  if (notFound) return <Navigate to={`/place/${slug}?from=country`} replace />;
  if (!country) {
    return (
      <div data-testid="country-loading" className="min-h-[60vh] flex items-center justify-center gap-3 text-[#2563EB]">
        <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Opening this country's portal…")}</span>
      </div>
    );
  }

  const toggleFollow = async () => {
    if (!user) return toast.info(tr("Sign in to follow places."));
    if (following) {
      await axios.delete(`${API}/follows/${encodeURIComponent(country.name)}`, { headers: authHeaders() });
      setFollowing(false);
    } else {
      await axios.post(`${API}/follows`, { place_name: country.name, place_type: "Country" }, { headers: authHeaders() });
      setFollowing(true);
      toast.success(`${tr("Following")} ${country.name}`);
    }
    window.dispatchEvent(new Event("wp:follows-changed"));
  };

  const hasPhoto = !!country.photo?.url;
  const desc = `${country.name} — ${country.official}. ${tr("Geography, history, economy, culture, food, travel and communities on WorldPulse.pro, the geographic home of the Social Pulse network.")}`;

  return (
    <div data-testid="country-page" className="pb-20">
      <Seo
        title={`${country.name} — WorldPulse.pro | ${country.region}`}
        description={desc}
        canonical={`https://worldpulse.pro/country/${country.slug}`}
        image={country.photo?.url}
        jsonLd={jsonLd}
      />

      <div className={`relative overflow-hidden ${hasPhoto ? "py-16 lg:py-24" : "py-12 lg:py-16"}`}
        style={!hasPhoto ? { background: "linear-gradient(135deg, #EAF4FD 0%, #F6FAFF 45%, #E8F7F0 100%)" } : undefined}>
        {hasPhoto && (
          <>
            <img data-testid="country-photo-hero" src={country.photo.url} alt={`${country.name} landscape`} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2557]/85 via-[#0F2557]/40 to-[#0F2557]/20" />
            <span className="absolute bottom-2 right-3 text-[0.62rem] font-bold text-white/75 z-10">
              📷 {country.photo.photographer_url ? (
                <>Photo by <a href={country.photo.photographer_url} target="_blank" rel="noreferrer" data-testid="hero-photographer-link" className="underline hover:text-white">{country.photo.credit}</a> on <a href={country.photo.source_url} target="_blank" rel="noreferrer" className="underline hover:text-white">Unsplash</a></>
              ) : country.photo.credit}
            </span>
          </>
        )}
        {country.cca2 && (
          <div data-testid="country-flag-badge" className="absolute top-5 right-5 z-10 hidden sm:block">
            <img src={`https://flagcdn.com/w160/${country.cca2.toLowerCase()}.png`} alt={`Flag of ${country.name}`}
              className="w-24 rounded-lg border-2 border-white/80 shadow-lg" />
            <div className={`text-center text-[0.6rem] font-extrabold tracking-widest uppercase mt-1.5 ${hasPhoto ? "text-white/90" : "text-[#475569]"}`}>{country.cca2} · {tr("Flag")}</div>
          </div>
        )}
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8">
          <nav className={`flex items-center gap-1.5 text-sm font-bold ${hasPhoto ? "text-white/90" : "text-[#2563EB]"}`} aria-label="Breadcrumb">
            <Link to="/" data-testid="country-breadcrumb-world" className="inline-flex items-center gap-1.5 hover:underline"><ArrowLeft size={14} /> {tr("World")}</Link>
            <span className="opacity-60">›</span>
            <Link to="/countries" data-testid="country-breadcrumb-region" className="hover:underline">{tr(country.region || "Countries")}</Link>
            <span className="opacity-60">›</span>
            <span className="opacity-90">{country.name}</span>
          </nav>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-5">
            <div>
              <span className={`text-[0.62rem] font-extrabold tracking-widest uppercase px-3 py-1 rounded-full ${hasPhoto ? "bg-white/15 text-white border border-white/30 backdrop-blur-sm" : "bg-white text-[#1D4ED8] border border-[#BFD4F2]"}`}>{tr("Country")}{country.subregion ? ` · ${tr(country.subregion)}` : ""}</span>
              <h1 data-testid="country-title" className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 ${hasPhoto ? "text-white drop-shadow-md" : "text-[#0F2557]"}`}>
                {country.flag} {country.name}
              </h1>
              <p className={`mt-2 text-sm font-semibold ${hasPhoto ? "text-white/85" : "text-[#475569]"}`}>
                {country.official}{country.natives?.length && country.natives[0] !== country.name ? ` · ${country.natives[0]}` : ""}
              </p>
              <div className={`flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm font-semibold ${hasPhoto ? "text-white/85" : "text-[#475569]"}`}>
                {country.capital && <span className="flex items-center gap-1.5"><Landmark size={14} /> {country.capital}</span>}
                {country.population && <span className="flex items-center gap-1.5"><Users size={14} /> {country.population.toLocaleString()}</span>}
                {country.latlng && <span className="font-mono-wp text-xs opacity-80">{country.latlng[0]?.toFixed(2)}°, {country.latlng[1]?.toFixed(2)}°</span>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {sections?.overview && <ListenButton text={`${country.name}. ${sections.overview.map((p) => tr(p)).join(" ")}`} label={tr("Listen")} />}
              {user && (
                <button data-testid="country-follow-button" onClick={toggleFollow}
                  className={`flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full border transition-all active:scale-[0.97] ${
                    following ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#DC2626]" : "bg-white border-[#86EFAC] text-[#15803D] hover:bg-[#F0FDF4]"}`}>
                  <Heart size={14} fill={following ? "currentColor" : "none"} /> {following ? tr("Unfollow") : tr("Follow")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-16 z-40 bg-white/95 border-b border-[#E5EDF7]" style={{ backdropFilter: "blur(10px)" }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8 flex gap-1 overflow-x-auto py-2" data-testid="country-anchor-nav">
          {SECTION_META.map((s) => (
            <a key={s.id} href={`#${s.id}`} data-testid={`country-anchor-${s.id}`}
              className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">
              {tr(s.title)}
            </a>
          ))}
          <a href="#cities" className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">{tr("Major Cities")}</a>
          <a href="#news" className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">{tr("News")}</a>
          <a href="#photos" className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">{tr("Photos")}</a>
          {statesInfo?.states?.length > 0 && (
            <a href="#states" className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">{tr(statesInfo.term)}</a>
          )}
          <a href="#community" className="shrink-0 text-xs font-bold text-[#334155] hover:text-[#2563EB] hover:bg-[#EAF2FD] px-3 py-1.5 rounded-full transition-colors">{tr("Community")}</a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {country.premium_portal && (
          <div data-testid="country-premium-banner" className="mt-8 border border-[#86EFAC] bg-[#F0FDF4] rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-extrabold text-[#15803D]">{tr("This country has its own dedicated portal")}</div>
              <p className="text-sm text-[#3F5548] mt-1">{tr("Full local coverage, data and communities live on")} <span className="font-extrabold">{country.premium_portal}</span></p>
            </div>
            <a href={`https://${country.premium_portal.toLowerCase()}`} target="_blank" rel="noreferrer"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-sm px-6 py-3 rounded-xl flex items-center gap-2 transition-colors">
              {tr("Open")} {country.premium_portal} <ExternalLink size={14} />
            </a>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2">
            {generating && (
              <div data-testid="country-generating" className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-3xl p-6 flex items-center gap-3 text-[#1D4ED8] mb-8">
                <Loader2 className="animate-spin shrink-0" size={18} />
                <span className="text-sm font-bold">{tr("Writing this country's portal sections from verified facts — about 30 seconds…")}</span>
              </div>
            )}
            {SECTION_META.map((meta) => {
              const paras = sections?.[meta.id];
              const Icon = meta.icon;
              return (
                <section key={meta.id} id={meta.id} className="scroll-mt-32 mb-12" data-testid={`country-section-${meta.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-[#EAF2FD] flex items-center justify-center"><Icon size={17} className="text-[#2563EB]" /></span>
                    <h2 className="text-xl font-extrabold text-[#0F2557] flex-1">{tr(meta.title)}</h2>
                    {paras?.length > 0 && <ListenButton compact text={`${tr(meta.title)}. ${paras.map((p) => tr(p)).join(" ")}`} />}
                  </div>
                  <div className="mt-4 space-y-4">
                    {paras?.length ? paras.map((p, i) => (
                      <p key={i} className="text-[1.05rem] text-[#1E293B] leading-[1.85]">{tr(p)}</p>
                    )) : (
                      <div className="space-y-2">
                        <div className="h-3.5 rounded-full bg-[#EAF2FD] animate-pulse w-full" />
                        <div className="h-3.5 rounded-full bg-[#EAF2FD] animate-pulse w-11/12" />
                        <div className="h-3.5 rounded-full bg-[#EAF2FD] animate-pulse w-4/5" />
                      </div>
                    )}
                  </div>
                  {meta.eco && paras?.length > 0 && (
                    <a href={meta.eco.url} target="_blank" rel="noreferrer" data-testid={`country-eco-link-${meta.id}`}
                      className="inline-flex items-center gap-2 mt-4 text-xs font-extrabold text-[#0D9488] bg-[#F0FDFA] border border-[#99F6E4] rounded-full px-4 py-2 hover:bg-[#CCFBF1] transition-colors">
                      {tr(meta.eco.label)} <ExternalLink size={12} />
                    </a>
                  )}
                </section>
              );
            })}

            <section id="cities" className="scroll-mt-32 mb-12" data-testid="country-section-cities">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#FFF7ED] flex items-center justify-center"><MapPin size={17} className="text-[#EA580C]" /></span>
                <h2 className="text-xl font-extrabold text-[#0F2557]">{tr("Major Cities")}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {cities.map((c) => (
                  <Link key={c.name} to={`/place/${c.name.toLowerCase().replace(/\s+/g, "-")}`} data-testid="country-city-card"
                    className="border border-[#E5EDF7] rounded-2xl p-4 hover:border-[#EA580C] hover:bg-[#FFF7ED] transition-colors group">
                    <div className="font-extrabold text-sm text-[#0F2557] group-hover:text-[#EA580C]">{c.name}</div>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{tr(c.role)}</p>
                  </Link>
                ))}
                {!cities.length && <p className="text-sm text-[#64748B]">{tr("City directory is being assembled.")}</p>}
              </div>
            </section>

            <CountryNews path={`/country/${country.slug}/news`} name={country.name} />

            <PhotoGallery gkey={`country-${country.slug}`} query={country.name} name={country.name} />

            {statesInfo?.states?.length > 0 && (
              <section id="states" className="scroll-mt-32 mb-12" data-testid="country-section-states">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-[#F0FDF4] flex items-center justify-center"><Landmark size={17} className="text-[#16A34A]" /></span>
                  <h2 className="text-xl font-extrabold text-[#0F2557]">{tr(statesInfo.term)}</h2>
                  <span className="text-xs font-bold text-[#64748B] bg-[#F1F6FD] rounded-full px-3 py-1">{statesInfo.states.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-4">
                  {statesInfo.states.map((s) => (
                    <Link key={s.slug} to={`/country/${country.slug}/${s.slug}`} data-testid="country-state-card"
                      className="border border-[#E5EDF7] rounded-xl px-3.5 py-2.5 hover:border-[#16A34A] hover:bg-[#F0FDF4] transition-colors group">
                      <div className="text-xs font-extrabold text-[#0F2557] group-hover:text-[#16A34A] leading-tight truncate">{s.name}</div>
                      <div className="text-[0.6rem] font-semibold text-[#64748B] mt-0.5 truncate">{tr(s.type)}{s.capital ? ` · ${s.capital}` : ""}</div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <Discussions dkey={country.slug} placeName={country.name} />

            {sections && (
              <div className="flex flex-wrap items-center gap-4">
                <p className="text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8]">{tr("AI-assisted portal content grounded in verified facts — community review welcome")}</p>
                <SuggestCorrection gkey={country.slug} placeName={country.name}
                  sections={["overview", "geography", "history", "government", "economy", "agriculture", "food", "culture", "education", "health", "transportation", "tourism", "cities", "facts", "photos", "other"]} />
              </div>
            )}
          </div>

          <div className="space-y-5">
            <QuickFacts c={country} tr={tr} />
            {country.wiki?.extract && (
              <div data-testid="country-wiki-card" className="border border-[#E2E8F0] rounded-3xl p-5 bg-white">
                <span className="font-bold text-sm text-[#334155]">📖 {tr("From Wikipedia")}</span>
                <p className="text-sm text-[#334155] leading-relaxed mt-2">{country.wiki.extract}</p>
                <a href={country.wiki.url} target="_blank" rel="noreferrer" data-testid="country-wiki-link"
                  className="inline-block mt-2 text-sm font-extrabold text-[#2563EB] hover:underline">
                  {tr("Read the full article on Wikipedia")} →
                </a>
              </div>
            )}
            {country.cca2 && (
              <div data-testid="country-map-card" className="border border-[#BFD4F2] rounded-3xl p-5 bg-[#F6FAFF]">
                <span className="font-bold text-sm text-[#1D4ED8]">🗺️ {tr("Map of")} {country.name}</span>
                <img src={`https://cdn.jsdelivr.net/gh/djaiss/mapsicon@master/all/${country.cca2.toLowerCase()}/vector.svg`}
                  alt={`Map outline of ${country.name}`} loading="lazy"
                  className="w-full max-h-44 object-contain mt-3 opacity-80"
                  onError={(e) => { e.target.closest('[data-testid="country-map-card"]').style.display = "none"; }} />
                <a href={`https://www.openstreetmap.org/#map=5/${country.latlng?.[0]}/${country.latlng?.[1]}`} target="_blank" rel="noreferrer"
                  className="inline-block mt-2 text-xs font-extrabold text-[#1D4ED8] hover:underline">
                  {tr("Open interactive map")} →
                </a>
              </div>
            )}
            <ShareBadge url={`https://worldpulse.pro/country/${country.slug}`} />
            {country.neighbors?.length > 0 && (
              <div data-testid="country-neighbors-card" className="border border-[#99F6E4] rounded-3xl p-5 bg-[#F0FDFA]">
                <span className="font-bold text-sm text-[#0D9488]">{tr("Neighboring countries")}</span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {country.neighbors.map((n) => (
                    <Link key={n.slug} to={`/country/${n.slug}`} data-testid="country-neighbor-chip"
                      className="text-xs font-bold text-[#0F2557] bg-white border border-[#CBD5E1] rounded-full px-3 py-1.5 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors">
                      {n.flag} {n.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="border border-[#D8CCF2] rounded-3xl p-5 bg-[#FAF5FF]">
              <span className="font-bold text-sm text-[#6D28D9]">{tr("Explore across the network")}</span>
              <div className="mt-3 space-y-2 text-xs font-bold">
                {[["TravelerPulse.pro", "https://travelerpulse.pro"], ["FarmersPulse.com", "https://farmerspulse.com"],
                  ["FoodPulse.net", "https://foodpulse.net"], ["NaturePulse.net", "https://naturepulse.net"],
                  ["PhotoPulse.pro", "https://photopulse.pro"]].map(([label, url]) => (
                  <a key={label} href={url} target="_blank" rel="noreferrer" className="flex items-center justify-between text-[#334155] hover:text-[#6D28D9] transition-colors">
                    {label} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
